import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { keyBy } from "@common/helpers/collections";
import { mapValues, omit, omitBy } from "@common/helpers/objects";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { useObserver } from "@hooks/useObserver";
import { useEntities } from "@hooks/useEntities";
import { useClasses } from "@api/class.api";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { SelectedAnswer, TaxAnswers } from "../TaxQuestions/types";
import { TransactionsFilters } from "../filters/lib";
import { ChildrenProps } from "../../../types";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "../PaginatedTransactionsTable.context";
import { checkSimilarTransactionsForBulkUpdateAndShowToastIfApplicable } from "../similarTransactionsUpdate";
import { useTaxQuestionsForBulkActions } from "./useTaxQuestionsForBulkActions";
import { useTaxSavingRulesForBulkActions } from "./useTaxSavingRulesForBulkActions";
import {
    BulkUpdateDetails,
    BulkUpdateMode,
    BulkUpdateParams,
    BulkUpdateStatus,
    preparePayloadsForFilters,
    preparePayloadsForSelectedTransactions,
} from "./lib";
import {
    SelectedTransaction,
    TransactionsBulkActionsContextProvider as BulkActionsProvider,
    TransactionsBulkActionsContextValue,
    UpdateSelectedOptions,
} from "./transactionsBulkActionsContext";
import { TransactionsUpdateToast } from "./TransactionsUpdateStatus/TransactionsUpdateToast";
import {
    mapTransactionToSelected,
    TOAST_MESSAGE_KEY,
} from "./TransactionsBulkActionsContextProvider.lib";
export interface TransactionsBulkActionsContextProviderProps extends ChildrenProps {
    onUpdated(
        updatedTransactions: TransactionListItem[],
        details: BulkUpdateDetails,
    ): void;
    totalTransactions: number;
    currentFilters: TransactionsFilters;
    /**
     * Review fields currently active for this task (e.g. a Transactions
     * Review task's selected review columns). When a bulk action sets a
     * value on one of these fields, the request is tagged with
     * `autoReviewField` so the backend auto-marks the field reviewed for
     * every changed transaction — the same eligibility-filtered mechanism
     * (`TransactionFieldReviewService`) the manual single-row review cells
     * already use on a successful inline edit. Omit outside review-task
     * contexts so plain bulk actions never auto-mark anything reviewed.
     */
    reviewFields?: TransactionReviewField[];
}

export const TransactionsBulkActionsContextProvider: React.FC<
    TransactionsBulkActionsContextProviderProps
> = ({
    onUpdated,
    children,
    currentFilters,
    totalTransactions,
    reviewFields,
}) => {
    const [answers, setAnswers] = useState<TaxAnswers>({} as TaxAnswers);

    const {
        transactions,
        updateDetails,
        currentView,
        disableSimilarTransactionsUpdate,
    } = useContext(PaginatedTransactionsDataContext);
    const { doUpdate, undo, setUpdateDetails } = useContext(
        PaginatedTransactionsActionsContext,
    );
    const { workspaceId } = useScope();

    const [selected, setSelected] = useState<
        Record<number, SelectedTransaction>
    >({});

    const selectedTransactions = useMemo<
        Record<number, SelectedTransaction>
    >(() => {
        if (!transactions) {
            return {};
        }

        const txById = new Map(transactions.map((t) => [t.id, t]));
        const result: Record<number, SelectedTransaction> = {};

        for (const idStr of Object.keys(selected)) {
            const id = Number(idStr);
            const transaction = txById.get(id);
            if (transaction) {
                result[id] = mapTransactionToSelected(transaction);
            }
        }

        return result;
    }, [selected, transactions]);

    const [filtersSelection, setFiltersSelection] =
        useState<TransactionsFilters | null>(null);

    const {
        notify: notifySelectionChanged,
        subscribe: subscribeToSelectionChanges,
    } = useObserver<void>();
    const { toast } = useToaster();

    const entities = useEntities();
    const { classesMap } = useClasses();

    const selectedArray = useMemo(
        () => Object.values(selectedTransactions),
        [selectedTransactions],
    );

    const selectedCount = useMemo(
        () =>
            filtersSelection
                ? totalTransactions
                : Object.keys(selectedTransactions).length,
        [filtersSelection, selectedTransactions, totalTransactions],
    );

    useEffect(() => {
        setAnswers((prev) =>
            Object.keys(prev).length === 0 ? prev : ({} as TaxAnswers),
        );
        notifySelectionChanged();
    }, [selectedCount, notifySelectionChanged]);

    useEffect(() => {
        setFiltersSelection(null);
    }, [currentFilters]);

    const taxRule = useTaxSavingRulesForBulkActions(
        selectedTransactions,
        filtersSelection,
    );
    const taxQuestions = useTaxQuestionsForBulkActions(
        selectedTransactions,
        filtersSelection,
        answers,
    );

    const isSelected: TransactionsBulkActionsContextValue["isSelected"] =
        useCallback(
            (transaction) => !!selectedTransactions[transaction.id],
            [selectedTransactions],
        );

    const select: TransactionsBulkActionsContextValue["select"] = useCallback(
        (transactionOrTransactions) => {
            const selectionUpdate: Record<number, SelectedTransaction> =
                Array.isArray(transactionOrTransactions)
                    ? keyBy(
                          transactionOrTransactions.map(
                              mapTransactionToSelected,
                          ),
                          "id",
                      )
                    : {
                          [transactionOrTransactions.id]:
                              mapTransactionToSelected(
                                  transactionOrTransactions,
                              ),
                      };

            setSelected((prev) => ({ ...prev, ...selectionUpdate }));
        },
        [],
    );
    const deselect: TransactionsBulkActionsContextValue["deselect"] =
        useCallback((transactionOrTransactions) => {
            if (Array.isArray(transactionOrTransactions)) {
                const dict = keyBy(transactionOrTransactions, "id");
                setSelected((prev) =>
                    omitBy(prev, (value) => value.id in dict),
                );
            } else {
                setSelected((prev) => omit(prev, transactionOrTransactions.id));
            }
        }, []);

    const updateSelected = useCallback(
        async (params: BulkUpdateParams, options?: UpdateSelectedOptions) => {
            // targetTransactionIds restricts the update to a caller-supplied
            // subset of the current selection (see
            // UpdateSelectedOptions/partitionSelectionByInvoiceOrBillLock) —
            // ignored in filters ("select all") mode, where there is no
            // loaded row list to restrict against.
            const targetIds =
                !filtersSelection && options?.targetTransactionIds
                    ? new Set(options.targetTransactionIds)
                    : null;
            const targetedSelectedTransactions: Record<
                number,
                SelectedTransaction
            > = targetIds
                ? Object.fromEntries(
                      Object.entries(selectedTransactions).filter(([id]) =>
                          targetIds.has(Number(id)),
                      ),
                  )
                : selectedTransactions;
            const targetedCount = targetIds ? targetIds.size : selectedCount;

            const payloads = filtersSelection
                ? preparePayloadsForFilters({
                      updateParams: params,
                      filters: {
                          ...filtersSelection,
                          entityIds:
                              filtersSelection.entityIds &&
                              filtersSelection.entityIds.length > 0
                                  ? filtersSelection.entityIds
                                  : entities.map((entity) => entity.id),
                      },
                      transactionCount: selectedCount,
                      activeReviewFields: reviewFields,
                      classesById: classesMap,
                  })
                : preparePayloadsForSelectedTransactions({
                      updateParams: params,
                      selected: targetedSelectedTransactions,
                      activeReviewFields: reviewFields,
                  });

            if (targetedCount === 0) {
                return;
            }

            try {
                const newDetails = {
                    status: BulkUpdateStatus.UPDATING,
                    transactionsCount: targetedCount,
                    mode: filtersSelection
                        ? BulkUpdateMode.FILTERS
                        : BulkUpdateMode.SELECTED,
                    ...params,
                };

                const result = await doUpdate(
                    newDetails,
                    payloads,
                    (transactionsUpdated, details) => {
                        if (details.mode === BulkUpdateMode.SELECTED) {
                            setSelected(
                                keyBy(
                                    transactionsUpdated.map(
                                        mapTransactionToSelected,
                                    ),
                                    "id",
                                ),
                            );
                        }
                        onUpdated(transactionsUpdated, details);
                    },
                );

                const getDisplayedChangedEntityName = () => {
                    if (params.update === "counterpartyId") {
                        return params.counterpartyName;
                    }
                    if (params.update === "customerId") {
                        return params.customerName;
                    }
                    if (params.update === "coa") {
                        return params.accountName;
                    }
                    if (params.update === "accrualCoa") {
                        return params.accountName;
                    }
                    return undefined;
                };

                toast(
                    <TransactionsUpdateToast
                        updateDetails={{
                            ...newDetails,
                            status: BulkUpdateStatus.SUCCESS,
                            transactionsCount:
                                result.updatedTransactions.length,
                        }}
                        previousTransactions={result.transactionsBeforeUpdate}
                        undo={undo}
                        displayedChangedEntityName={getDisplayedChangedEntityName()}
                    />,
                    TOAST_MESSAGE_KEY,
                );

                if (!disableSimilarTransactionsUpdate && workspaceId) {
                    checkSimilarTransactionsForBulkUpdateAndShowToastIfApplicable(
                        {
                            workspaceId,
                            canonicalNormalizedDescriptionId:
                                result.canonicalNormalizedDescriptionId ?? null,
                            updatedTransactions: result.updatedTransactions,
                            transactionsBeforeUpdate:
                                result.transactionsBeforeUpdate,
                            params,
                            toast,
                            doUpdate,
                            undo,
                            displayedChangedEntityName:
                                getDisplayedChangedEntityName(),
                            view: currentView,
                        },
                    ).catch(() => {});
                }

                return result;
            } catch {
                setUpdateDetails((prev) => ({
                    ...prev!,
                    status: BulkUpdateStatus.ERROR,
                }));
            }
        },
        [
            filtersSelection,
            entities,
            selectedCount,
            selectedTransactions,
            doUpdate,
            toast,
            undo,
            onUpdated,
            setUpdateDetails,
            disableSimilarTransactionsUpdate,
            workspaceId,
            currentView,
            reviewFields,
            classesMap,
        ],
    );

    const handleAnswer = useCallback(
        (selectedAnswers: SelectedAnswer[]) => {
            setAnswers((prev) => ({
                ...prev,
                ...mapValues(keyBy(selectedAnswers, "key"), "answer"),
            }));

            return updateSelected({
                update: "taxQuestionAnswers",
                value: selectedAnswers,
            });
        },
        [updateSelected],
    );

    const clearSelection: TransactionsBulkActionsContextValue["clearSelection"] =
        useCallback(() => {
            setSelected({});
            setFiltersSelection(null);
        }, []);

    const selectByCurrentFilters = useCallback(() => {
        clearSelection();
        setFiltersSelection(currentFilters);
    }, [clearSelection, currentFilters]);

    const bulkActionsContextValue = useMemo(
        () => ({
            enabled: true,
            select,
            selected: selectedTransactions,
            selectedArray,
            deselect,
            clearSelection,
            isSelected,
            selectedCount,
            updateDetails,
            updateSelected,
            subscribeToSelectionChanges,
            taxRule,
            answers,
            taxQuestions,
            handleAnswer,
            selectAll: selectByCurrentFilters,
            hasSelectedAll: !!filtersSelection,
            filtersSelection,
            totalTransactions,
        }),
        [
            answers,
            clearSelection,
            selectedTransactions,
            selectedArray,
            deselect,
            filtersSelection,
            handleAnswer,
            isSelected,
            select,
            selectByCurrentFilters,
            selectedCount,
            subscribeToSelectionChanges,
            taxQuestions,
            taxRule,
            totalTransactions,
            updateDetails,
            updateSelected,
        ],
    );

    return (
        <BulkActionsProvider value={bulkActionsContextValue}>
            {children}
        </BulkActionsProvider>
    );
};
